import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import butterflyApi from '../ButterflyApi';
import MobilePage from '../UiComponents/MobilePage';
import { isLogin } from '../Utils/Cookies';
import {
  CATEGORY_LABELS,
  INQUIRY_CATEGORIES,
  INQUIRY_STATUSES,
  STATUS_LABELS,
  formatInquiryDate,
  getApiErrorMessage,
  normalizeInquiryList,
} from './supportInquiryUtils';
import './SupportInquiry.css';

const SupportInquiryList = () => {
  const [inquiries, setInquiries] = useState([])
  const [status, setStatus] = useState('')
  const [category, setCategory] = useState('')
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')

  const load = useCallback(async () => {
    if (!isLogin()) {
      setLoading(false)
      return
    }

    setLoading(true)
    setErrorMessage('')
    const response = await butterflyApi.getSupportInquiries(status, category, error => {
      setErrorMessage(getApiErrorMessage(error, '문의 내역을 불러오지 못했습니다.'))
    })

    setInquiries(normalizeInquiryList(response))
    setLoading(false)
  }, [status, category])

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 })
    load()
  }, [load])

  if (loading) {
    return <MobilePage className='storybook-page inquiry-page' loading loadingMessage="문의 내역을 불러오고 있습니다." />
  }

  return <MobilePage className='storybook-page inquiry-page'>
    <main className='inquiry-content'>
      <Link className='book-text-link inquiry-back' to='/'><span aria-hidden='true'>‹</span> 홈으로</Link>
      <header className='inquiry-intro'>
        <p className='book-eyebrow'><span aria-hidden='true'>✦</span> 작은 궁금함도 편하게 남겨주세요</p>
        <div className='inquiry-title-row'>
          <h1 className='book-title'>1:1 문의</h1>
          <span className='inquiry-intro-icon' aria-hidden='true'>?</span>
        </div>
        <p className='book-description'>궁금한 점이나 도움이 필요한 순간,<br />텔링파파가 함께할게요.</p>
        {isLogin() && <Link className='book-primary-button inquiry-write' to='/support/inquiries/new'>새 문의 작성하기 <span aria-hidden='true'>＋</span></Link>}
      </header>

      {!isLogin() && <section className='inquiry-empty' aria-labelledby='inquiry-login-title'>
        <span className='inquiry-empty-icon' aria-hidden='true'>✧</span>
        <h2 id='inquiry-login-title'>로그인하고 이야기를 남겨주세요</h2>
        <p>로그인하면 문의를 작성하고<br />내가 남긴 문의와 답변을 확인할 수 있어요.</p>
        <Link className='book-primary-button' to='/login?redirect=/support/inquiries'>로그인하기 <span aria-hidden='true'>→</span></Link>
      </section>}

      {isLogin() && <section className='inquiry-section' aria-labelledby='inquiry-list-title'>
        <div className='inquiry-section-heading'>
          <div>
            <h2 id='inquiry-list-title'>나의 문의</h2>
            <p>남겨주신 이야기와 답변을 모았어요.</p>
          </div>
          {!errorMessage && <span className='inquiry-count'>{inquiries.length}건</span>}
        </div>
        <div className='inquiry-filters'>
          <label htmlFor='inquiry-filter-status'>문의 상태
            <select
              id='inquiry-filter-status'
              className='inquiry-control'
              value={status}
              onChange={event => setStatus(event.target.value)}
            >
              <option value=''>전체 상태</option>
              {INQUIRY_STATUSES.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <label htmlFor='inquiry-filter-category'>문의 유형
            <select
              id='inquiry-filter-category'
              className='inquiry-control'
              value={category}
              onChange={event => setCategory(event.target.value)}
            >
              <option value=''>전체 유형</option>
              {INQUIRY_CATEGORIES.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
        </div>

        {errorMessage && <div className='book-error inquiry-error' role='alert'>{errorMessage}</div>}

        {!errorMessage && inquiries.length === 0 && <div className='inquiry-empty'>
          <span className='inquiry-empty-icon' aria-hidden='true'>✧</span>
          <h3>{status || category ? '조건에 맞는 문의가 없어요' : '아직 남긴 문의가 없어요'}</h3>
          <p>{status || category ? '다른 상태나 유형을 선택해 다시 확인해 보세요.' : <>궁금한 점을 편하게 남겨주세요.<br />내용을 확인하고 답변드릴게요.</>}</p>
          {!status && !category && <Link className='book-primary-button' to='/support/inquiries/new'>첫 문의 작성하기 <span aria-hidden='true'>＋</span></Link>}
        </div>}

        {!errorMessage && inquiries.length > 0 && <ul className='inquiry-list'>
          {inquiries.map(inquiry => <li key={inquiry.inquiryNo}>
            <Link className='inquiry-card' to={`/support/inquiries/${inquiry.inquiryNo}`}>
              <div className='inquiry-card-meta'>
                <span className='inquiry-category'>{CATEGORY_LABELS[inquiry.category] || inquiry.category}</span>
                <span className='inquiry-status' data-status={inquiry.status}>
                  <span className='inquiry-status-dot' aria-hidden='true' />{STATUS_LABELS[inquiry.status] || inquiry.status}
                </span>
              </div>
              <h3>{inquiry.title}</h3>
              <dl className='inquiry-card-date'>
                <div>
                  <dt>접수일시</dt>
                  <dd>{inquiry.registerDatetime ? <time dateTime={inquiry.registerDatetime}>{formatInquiryDate(inquiry.registerDatetime)}</time> : '—'}</dd>
                </div>
              </dl>
              <div className={`inquiry-card-action${inquiry.hasAnswer ? ' inquiry-card-action--answered' : ''}`}>
                <span>{inquiry.hasAnswer ? '답변 확인하기' : '문의 상세보기'}</span><span aria-hidden='true'>→</span>
              </div>
            </Link>
          </li>)}
        </ul>}
      </section>}
      <p className='inquiry-footnote'><span aria-hidden='true'>✦</span> 남겨주신 이야기에 귀 기울일게요.</p>
    </main>
  </MobilePage>
}

export default SupportInquiryList
